import { format, formatDistance, formatDistanceStrict } from 'date-fns';

/**
 * Formats the proved date into a readable format. e.g. 29 Apr, 2022
 * @param date Date to format
 * @returns Formatted date
 */
export function formatDate(date: Date | string): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        return format(new Date(date), `d LLL, yyyy`);
    } catch (e) {
        return '';
    }
}

/**
 * Formats date into dd/mm/yyyy format
 *
 * @param date Date to format
 * @returns Formatted date
 */
export function formatDateNumeric(date: Date | string, isReverse?: boolean): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        return format(new Date(date), isReverse ? `yyyy-MM-dd` : `dd/MM/yyyy`);
    } catch (e) {
        return '';
    }
}

/**
 * Formats the proved date into a readable format. e.g. 29 Apr, 2022 at 8:04 pm
 * @param date Date to format
 * @returns Formatted date and time
 */
export function formatDateTime(date: Date | string): string {
    if (typeof date === 'string') {
        date = date.replace(' ', 'T');
    }
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        return format(new Date(date), `d LLL, yyyy 'at' h:mm aaa`);
    } catch (error) {
        return '';
    }
}

/**
 * Calculates the distance between the provided date and the current date. e.g 3 days ago
 * @param date Date to calculate relative time
 * @returns Relative time compared to the current datetime
 */
export function calcRelativeTime(date: Date | string, strict?: boolean): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        if (typeof date === 'string') {
            date = date.replace(' ', 'T');
        }
        if (strict) {
            return formatDistanceStrict(new Date(date), new Date(), { addSuffix: true });
        }
        return formatDistance(new Date(date), new Date(), { addSuffix: true });
    } catch (error) {
        return '';
    }
}

/**
 * Extract only YYYY-MM-DD part out of the Date
 * @param date Date to be filtered
 * @returns Extracted part of the date
 */
export function parseDate(date: Date | string): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        if (typeof date === 'string') {
            date = date.replace(' ', 'T');
        }
        return new Date(date).toISOString().slice(0, 10);
    } catch (error) {
        return '';
    }
}

/**
 * Extract only YYYY-MM-DD part out of the date and converts to local timezone
 * @param date Date to be filtered
 * @returns Extracted part of the date
 */
export function parseLocalDate(date: Date | string): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        if (typeof date === 'string') {
            date = date.replace(' ', 'T');
        }
        const formattedDate = new Date(date).toLocaleDateString().slice(0, 10).split('/').reverse().join('-');

        return formattedDate;
    } catch (error) {
        return '';
    }
}

/**
 * Extract only YYYY-MM-DD part out of the Date
 * @param date Date to be filtered
 * @returns Extracted part of the date
 */
export function parseStringDate(date: string): string {
    // This is to prevent Safari from crashing
    // TODO: WP-1526
    try {
        if (typeof date === 'string') {
            date = date.replace(' ', 'T');
        }
        return date.slice(0, 10);
    } catch (error) {
        return '';
    }
}

/**
 * Capitalise only first letter of each word
 * @param word Word to be formatted
 * @returns Word capitalised accordingly
 */
export function formatMixedCase(word: string = ''): string {
    word = word.replaceAll('_', ' ');
    const splitStr = word.toLowerCase().split(' ');
    for (let i = 0; i < splitStr.length; i++) {
        splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join(' ');
}

/**
 * Turn camel case word into normal phrase
 *
 * @param word Word to be formatted
 * @returns Word capitalised accordingly
 */
export function unCamelCase(word: string): string {
    return (
        word
            // insert a space before all caps
            .replace(/([A-Z])/g, ' $1')
            // uppercase the first character
            .replace(/^./, function (str) {
                return str.toUpperCase();
            })
    );
}

/**
 * Turn normal phrase into camel case word
 *
 * @param word Word to be formatted
 * @returns Word capitalised accordingly
 */
export function camelCase(word: string): string {
    return (
        word
            // convert all letters to lower case
            .toLowerCase()
            // split by space
            .split(' ')
            // convert first letter to upper case
            .reduce((s, c) => s + (c.charAt(0).toUpperCase() + c.slice(1)))
    );
}

/**
 * Get first character of each word in a title, removing unnecessary words
 * @param title Title to be formattaed
 * @returns First character of each word in a string
 */
export function parseTitle(title: string, characters: number = 2): string {
    const _title = title.replaceAll('-', ' ');

    const ignore = ['the', 'and', 'pty', 'ltd', '&', '(', ')'];

    const acronym = _title.split(' ').map(function (item) {
        if (ignore.includes(item.toLowerCase())) {
            return '';
        } else return item[0]; // First letter of word
    });
    return acronym.join('').toUpperCase().substring(0, characters);
}
